/**
 * Registry du contenu d'aide pour chaque module — i18n FR/EN/DE.
 *
 * Structure :
 *   moduleKey: {
 *     fr: { title, purpose, how_it_works, tabs:[{name, description}], key_actions, related_modules },
 *     en: {...},
 *     de: {...}
 *   }
 *
 * Mode additif : ajouter de nouveaux modules sans toucher aux existants.
 */
export const MODULE_HELP_CONTENT = {
  origination: {
    fr: {
      title: "Origination — Pipeline deals",
      purpose:
        "Le module Origination est le centre névralgique de votre activité d'origination. Il pilote le pipeline des deals en 7 étapes (identification → contrôle), pondéré par probabilité de closing. Il croise le pipeline avec votre annuaire d'entités, votre doctrine, et désormais des opportunités générées automatiquement par NOAM+Tavily.",
      how_it_works:
        "Chaque deal a un ticket en M€, une étape, une probabilité de closing et est éventuellement relié à des entités de l'Annuaire (apporteurs, contacts, conseillers). Le pipeline pondéré = somme(ticket × prob_closing). La carte des frictions identifie les blocages structurels. La fractale ↑ remonte du Micro (deal isolé) vers le Macro (thèse d'investissement).",
      tabs: [
        {
          name: "Pipeline",
          description: "Kanban 7 étapes : identification, qualification, structuration, crédibilisation, financement, participation, contrôle. Chaque carte montre ticket, secteur, owner, % closing, et entités Annuaire reliées.",
        },
        {
          name: "Position dans la chaîne",
          description: "Visualise où chaque deal s'inscrit dans une chaîne causale (Dans-quel-but ↑ · Comment ↓). Permet de relier opérationnel et stratégique.",
        },
        {
          name: "Fractale ↑",
          description: "Vue fractale ascendante : remonte du Micro (deal individuel) au Méso (segment, thématique) puis Macro (positionnement institutionnel). Utile pour voir si vos deals racontent une histoire cohérente.",
        },
        {
          name: "Carte des frictions",
          description: "Heatmap croisant étapes × secteurs. Identifie où votre pipeline accumule de la friction (stagnations, conversions faibles, ressources sous-exploitées).",
        },
        {
          name: "Opportunités détectées",
          description: "Opportunités générées automatiquement par NOAM+Tavily en croisant votre contexte interne (annuaire, pipeline, doctrine, zone) × tendances externes. Pour chaque opportunité : taille marché, score, persona cible, chemin d'accès, connexion Annuaire, signal d'alerte précoce. Bouton 'Pousser dans le pipeline' pour créer un deal immédiat.",
        },
      ],
      key_actions: [
        "Ajouter une opportunité manuellement (Pipeline → Ajouter)",
        "Régénérer le scan d'opportunités contextualisées (24h cache)",
        "Pousser une opportunité détectée dans le pipeline → deal en stade identification",
        "Importer un lot via Excel/CSV ou IA conversationnelle",
      ],
      related_modules: ["Annuaire", "Négociation", "Doctrine", "Chaînage causal", "Risques"],
    },
    en: {
      title: "Origination — Deal Pipeline",
      purpose:
        "Origination is your deal flow nerve center. It runs a 7-stage pipeline (identification → control), weighted by closing probability, crossed with your Directory entities, doctrine, and now AI-generated opportunities.",
      how_it_works:
        "Each deal has a ticket size (M€), stage, closing probability, and may be linked to Directory entities (introducers, contacts, advisors). Weighted pipeline = sum(ticket × prob). The friction map identifies structural blockages. The ascending fractal moves from Micro (single deal) to Macro (investment thesis).",
      tabs: [
        { name: "Pipeline", description: "Kanban with 7 stages. Each card: ticket, sector, owner, % closing, linked Directory entities." },
        { name: "Position in chain", description: "Shows where each deal fits in a causal chain (What-for ↑ · How ↓)." },
        { name: "Fractal ↑", description: "Ascending fractal view: from Micro deal to Méso (theme) to Macro (positioning)." },
        { name: "Friction map", description: "Heatmap of stages × sectors. Spots structural friction in your pipeline." },
        { name: "Detected opportunities", description: "AI-generated opportunities crossing your internal context × external trends. Push to pipeline button creates a deal instantly." },
      ],
      key_actions: ["Add an opportunity", "Refresh opportunity scan", "Push detected opp to pipeline", "Import via Excel/CSV or conversational AI"],
      related_modules: ["Directory", "Negotiation", "Doctrine", "Causal chain", "Risks"],
    },
    de: {
      title: "Origination — Deal-Pipeline",
      purpose: "Origination ist das Nervenzentrum Ihrer Deal-Aktivität: 7-stufige Pipeline, gewichtet nach Abschlusswahrscheinlichkeit, gekreuzt mit Verzeichnis, Doktrin und KI-generierten Gelegenheiten.",
      how_it_works: "Jeder Deal hat Ticket (M€), Stufe, Wahrscheinlichkeit und ggf. Verzeichnis-Verbindungen. Gewichtete Pipeline = Summe(Ticket × Wahrscheinlichkeit).",
      tabs: [
        { name: "Pipeline", description: "Kanban mit 7 Stufen." },
        { name: "Position in der Kette", description: "Wo jeder Deal in einer Kausalkette steht." },
        { name: "Fraktal ↑", description: "Vom Mikro-Deal zum Makro-Positionierung." },
        { name: "Friktionskarte", description: "Heatmap Stufen × Sektoren." },
        { name: "Erkannte Chancen", description: "KI-generierte Chancen. Button 'In Pipeline schieben'." },
      ],
      key_actions: ["Chance hinzufügen", "Chancen-Scan neu generieren", "Erkannte Chance in Pipeline schieben"],
      related_modules: ["Verzeichnis", "Verhandlung", "Doktrin", "Kausalkette", "Risiken"],
    },
  },

  intelligence: {
    fr: {
      title: "IA",
      purpose:
        "L'IA est l'interface de raisonnement étendu de Noam. Elle permet de poser des questions complexes à NOAM Sonnet 4.5 en lui donnant accès à 10 modules de votre environnement + votre doctrine versionnée + des documents que vous chargez à la volée.",
      how_it_works:
        "Chat long avec multi-documents, conscience complète de l'environnement, 5 raccourcis pré-cadrés, archivage en briefs PDF. Le pattern async bypasse les timeouts proxy : les requêtes NOAM peuvent durer plusieurs minutes sans erreur 502.",
      tabs: [
        { name: "Raisonnement V2", description: "Drag&drop jusqu'à 30 fichiers (PDF/DOCX/XLSX/CSV/PPTX/images) en pool parallèle de 3. Bouton micro pour dicter votre question (Web Speech API). 5 raccourcis : Analyser pipeline, Préparer comité, Identifier leviers, Tester hypothèse, Scénarios marché. Chaque réponse peut être archivée en brief PDF dans la Bibliothèque." },
      ],
      key_actions: [
        "Drag&drop vos fichiers de projet → Noam les analyse en croisant avec votre environnement",
        "Cliquer un raccourci pour déclencher une analyse pré-cadrée",
        "Dicter votre question via le bouton micro (Chrome/Edge/Safari)",
        "Archiver une réponse en brief PDF dans la Bibliothèque",
        "Sauvegarder les fichiers de session dans Noam Drive",
      ],
      related_modules: ["Doctrine", "Annuaire", "Pipeline", "Risques", "Bibliothèque de briefs"],
    },
    en: {
      title: "AI",
      purpose: "Extended reasoning interface with NOAM Sonnet 4.5, full environment awareness + versioned doctrine + multi-document upload.",
      how_it_works: "Long chat with multi-docs, full environment awareness, 5 shortcuts, archive answers as PDF briefs. Async pattern bypasses proxy timeouts.",
      tabs: [
        { name: "Reasoning V2", description: "Drag&drop up to 30 files in parallel. Mic button to dictate. 5 shortcuts. Reply archivable as PDF brief." },
      ],
      key_actions: ["Drag&drop project files", "Click a shortcut", "Dictate via mic", "Archive reply as brief", "Save files to Noam Drive"],
      related_modules: ["Doctrine", "Directory", "Pipeline", "Risks", "Briefs library"],
    },
    de: {
      title: "KI",
      purpose: "Erweiterte Reasoning-Oberfläche mit NOAM Sonnet 4.5, voller Umgebungs-Zugriff + versionierte Doktrin + Multi-Dokument-Upload.",
      how_it_works: "Langer Chat mit Multi-Dokumenten, vollem Umgebungs-Bewusstsein, 5 Verknüpfungen, Antworten als PDF-Briefings archivierbar. Async-Pattern umgeht Proxy-Timeouts.",
      tabs: [
        { name: "Reasoning V2", description: "Drag&drop bis 30 Dateien parallel. Mikrofon-Taste zum Diktieren. 5 Verknüpfungen." },
      ],
      key_actions: ["Dateien per Drag&drop", "Verknüpfung anklicken", "Über Mikrofon diktieren"],
      related_modules: ["Doktrin", "Verzeichnis", "Pipeline", "Risiken"],
    },
  },

  radar: {
    fr: {
      title: "Noam Radar — Veille passive",
      purpose:
        "Le Radar est votre vigie. Il scanne automatiquement les signaux pertinents sur vos entités prioritaires, sur la géographie monde × secteurs, sur les patterns de transformation des marchés, et identifie les acteurs fantômes (qui devraient être dans votre annuaire mais ne le sont pas encore).",
      how_it_works:
        "Tavily + NOAM scannent chaque semaine. Les signaux sont scorés en criticité (faible/moyen/élevé/critique), classés par catégorie (macro, géopo, réglementaire, marchés, tech, climat…) et région. Vous pouvez demander à NOAM d'évaluer l'impact spécifique de chaque signal sur votre organisation.",
      tabs: [
        { name: "Mon écosystème", description: "Signaux ciblés sur les 20 entités Annuaire les plus stratégiques (engagées via deal, négo, mandat, ou taggées watchlist)." },
        { name: "Scan mondial", description: "Balayage géographie × secteur du monde entier. Configurez les zones et secteurs à surveiller." },
        { name: "Fantômes", description: "Acteurs détectés dans la veille mais absents de votre Annuaire — opportunité de complétude." },
        { name: "Transformations marché", description: "Signaux d'alerte précoce sur les transformations sectorielles à 6-36 mois : désintermédiation, réglementation, consolidation, comportement, nouveaux entrants. Pour chaque signal : biais du survivant inversé + action pendant la latence." },
      ],
      key_actions: [
        "Scanner mes entités (manuel ou cron hebdomadaire)",
        "Reclasser l'historique selon la Doctrine fractale",
        "Demander un impact évalué par NOAM sur chaque signal",
        "Scanner les transformations de marché (cache 24h)",
      ],
      related_modules: ["Annuaire", "Doctrine", "Cartographie 360", "Intelligence IA"],
    },
    en: {
      title: "Noam Radar — Passive intelligence",
      purpose: "Automatic scanning of relevant signals on your priority entities, world geo × sectors, market transformations, and ghost actors.",
      how_it_works: "Tavily + NOAM scan weekly. Signals scored by criticality, classified by category and region.",
      tabs: [
        { name: "My ecosystem", description: "Targeted signals on top 20 strategic Directory entities." },
        { name: "World scan", description: "Geography × sector world sweep." },
        { name: "Ghosts", description: "Actors detected in the watch but missing from your Directory." },
        { name: "Market transformations", description: "Early-warning signals on sectoral shifts (6-36 months). Survivor-bias-inverted + latency action." },
      ],
      key_actions: ["Scan my entities", "Reclassify history", "Request NOAM impact assessment", "Scan market transformations"],
      related_modules: ["Directory", "Doctrine", "360 Cartography", "AI Intelligence"],
    },
    de: {
      title: "Noam Radar — Passive Beobachtung",
      purpose: "Automatisches Scannen relevanter Signale.",
      how_it_works: "Tavily + NOAM scannen wöchentlich.",
      tabs: [
        { name: "Mein Ökosystem", description: "Signale zu 20 strategischen Entitäten." },
        { name: "Weltscan", description: "Geo × Sektor weltweit." },
        { name: "Geister", description: "Akteure die fehlen." },
        { name: "Markttransformationen", description: "Frühwarnsignale 6-36 Monate." },
      ],
      key_actions: ["Entitäten scannen", "Geschichte reklassifizieren"],
      related_modules: ["Verzeichnis", "Doktrin"],
    },
  },

  immobilier: {
    fr: {
      title: "Immobilier",
      purpose:
        "Pilote votre portefeuille immobilier : valorisation, dette, LTV par actif, matrice de faisabilité promoteur, comparaison vs prix médians commune. Lecture IA + génération de PDF de synthèse.",
      how_it_works:
        "Chaque actif est typé (résidentiel, commercial, mixte, terrain, rendement) avec phase (acquisition/détention/cession), valorisation et dette. Le LTV global est recalculé en continu. La taille de marché contextuelle (widget) replace votre portefeuille dans le marché immobilier total.",
      tabs: [
        { name: "Portefeuille", description: "CRUD des actifs immobiliers. Vue cartes avec LTV coloré (rouge >75%, orange >65%, vert sinon)." },
        { name: "Valorisation", description: "Comparaison de chaque actif vs prix médian commune avec sources publiques." },
        { name: "Promotion", description: "Matrice de faisabilité pour projets promoteurs (foncier + construction + revente)." },
        { name: "Sources publiques", description: "Sources de données publiques utilisées pour la valorisation." },
      ],
      key_actions: ["Ajouter un actif", "Importer un lot via Excel/IA", "Demander une analyse IA + PDF"],
      related_modules: ["Financement", "Courtage", "Origination", "Risques"],
    },
    en: {
      title: "Real Estate",
      purpose: "Manage your real estate portfolio: valuation, debt, LTV per asset, developer feasibility matrix.",
      how_it_works: "Each asset typed with phase, valuation, debt. Global LTV continuously recalculated.",
      tabs: [
        { name: "Portfolio", description: "Asset CRUD with color-coded LTV." },
        { name: "Valuation", description: "Compare each asset vs municipal median." },
        { name: "Development", description: "Feasibility matrix for developer projects." },
        { name: "Public sources", description: "Public data sources used." },
      ],
      key_actions: ["Add asset", "Import via Excel/AI", "Request AI analysis + PDF"],
      related_modules: ["Financing", "Brokerage", "Origination", "Risks"],
    },
    de: {
      title: "Immobilien",
      purpose: "Verwalten Sie Ihr Immobilienportfolio.",
      how_it_works: "Jeder Vermögenswert typisiert mit Phase, Bewertung, Schulden.",
      tabs: [
        { name: "Portfolio", description: "Vermögenswerte mit LTV." },
        { name: "Bewertung", description: "Vergleich mit kommunalem Median." },
        { name: "Entwicklung", description: "Machbarkeitsmatrix." },
        { name: "Öffentliche Quellen", description: "Datenquellen." },
      ],
      key_actions: ["Vermögenswert hinzufügen"],
      related_modules: ["Finanzierung", "Maklertätigkeit"],
    },
  },

  courtage: {
    fr: {
      title: "Courtage",
      purpose:
        "Pilote vos mandats de courtage immobilier et de cession d'entreprise. Pipeline visuel par statut, qualification (score 0-100), valorisation par multiples, lecture IA NOAM pour identifier acheteurs stratégiques et fourchette de valo.",
      how_it_works:
        "Chaque mandat a un type (courtage_immo, cession_entreprise, acquisition, M&A), un statut (prospection → cloturé), une qualification score, une valeur transaction et une commission estimée. L'IA NOAM peut proposer une fiche acheteurs cibles.",
      tabs: [
        { name: "Tableau de bord", description: "KPI globaux : mandats actifs, volume pipeline, commission estimée, taux de conversion." },
        { name: "Mandats", description: "Liste des mandats avec filtre par statut. Cliquer ouvre la fiche détaillée." },
      ],
      key_actions: ["Nouveau mandat", "Qualifier (0-100)", "Demander lecture IA NOAM", "Identifier acheteurs stratégiques"],
      related_modules: ["Annuaire", "Origination", "Négociation"],
    },
    en: { title: "Brokerage", purpose: "Real estate and M&A mandate management.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Maklertätigkeit", purpose: "Maklerverwaltung.", tabs: [], key_actions: [], related_modules: [] },
  },

  financement: {
    fr: {
      title: "Financement — Capital",
      purpose:
        "Module de pilotage du capital : finançabilité globale, LTV par actif, séquençage des 6 phases (Diagnostic, Structuration, Sécurisation, Financement, Expansion, Consolidation). Le widget Taille de marché replace votre activité financement dans le marché total.",
      how_it_works:
        "Chaque actif a une valeur, une LTV (0-1), une garantie, une phase, une classe et une localisation. Le score de finançabilité global est calculé sur l'ensemble du portefeuille.",
      tabs: [],
      key_actions: ["Ajouter un actif", "Importer via Excel/IA", "Suivre les 6 phases"],
      related_modules: ["Immobilier", "Courtage", "Risques", "Doctrine"],
    },
    en: { title: "Financing", purpose: "Capital module: financeability, LTV per asset.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Finanzierung", purpose: "Kapital-Modul.", tabs: [], key_actions: [], related_modules: [] },
  },

  metacognition: {
    fr: {
      title: "Métacognition",
      purpose:
        "Noam analyse Noam. Détecte les améliorations tactiques à apporter à la plateforme + cartographie votre positionnement marché vs vos vrais concurrents (Palantir, Aladdin, Sigma, Hex…). C'est l'auto-réflexion de la doctrine versionnée.",
      how_it_works:
        "NOAM scanne en arrière-plan les frictions internes et propose des suggestions tactiques scorées par impact × effort. Un radar à 5 axes positionne Noam : Profondeur analytique, Couverture sectorielle, Intelligence doctrinale, Rapidité déploiement, Accessibilité prix.",
      tabs: [
        { name: "Suggestions tactiques", description: "Améliorations détectées par Noam sur Noam (ex: ajouter un champ, créer un cross-link)." },
        { name: "Radar marché", description: "Positionnement Noam vs Palantir, Aladdin, Sigma, Causal, Hex sur 5 axes." },
        { name: "Auto-specs", description: "Generation automatique de specs techniques (drawer)." },
      ],
      key_actions: ["Lancer un scan métacognitif", "Régénérer le radar marché", "Approuver/rejeter une suggestion"],
      related_modules: ["Doctrine", "Toutes (analyse cross-modules)"],
    },
    en: { title: "Metacognition", purpose: "Noam analyzes Noam.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Metakognition", purpose: "Noam analysiert Noam.", tabs: [], key_actions: [], related_modules: [] },
  },

  annuaire: {
    fr: {
      title: "Annuaire",
      purpose:
        "Votre carnet d'entités stratégiques : personnes, sociétés, institutions, family offices, conseillers. Chaque entité peut être reliée à des deals, négociations, mandats, risques. C'est le tissu relationnel de votre organisation.",
      how_it_works:
        "Chaque entité est typée (personne/société/...) et peut avoir des liens vers les autres modules. L'IA peut importer en lot depuis Excel, PDF ou texte libre. Les Phantoms (Radar) suggèrent des entités à ajouter.",
      tabs: [
        { name: "Liste", description: "Vue tabulaire avec filtres par type, secteur, statut." },
        { name: "Graph", description: "Vue graphe des relations entre entités et modules." },
      ],
      key_actions: ["Ajouter une entité", "Importer un lot via IA", "Fusionner doublons", "Voir cartographie 360"],
      related_modules: ["Origination", "Négociation", "Courtage", "Radar"],
    },
    en: { title: "Directory", purpose: "Your relational fabric.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Verzeichnis", purpose: "Ihr Beziehungsgeflecht.", tabs: [], key_actions: [], related_modules: [] },
  },

  doctrine: {
    fr: {
      title: "Doctrine",
      purpose:
        "Le moteur épistémologique de Noam. Une doctrine versionnée de 46+ principes qui guide TOUS les raisonnements IA de la plateforme. Si vous ne définissez pas votre doctrine, Noam tourne avec sa doctrine par défaut (Constante Partners).",
      how_it_works:
        "Chaque principe peut être actif/inactif. La version est trackée. L'IA NOAM consulte la doctrine active à chaque appel — c'est ce qui rend les réponses ancrées plutôt que génériques.",
      tabs: [],
      key_actions: [
        "Activer une doctrine pré-définie",
        "Ajouter/éditer un principe",
        "Versionner une nouvelle doctrine",
        "Tester un principe sur un cas concret",
      ],
      related_modules: ["Intelligence IA", "Métacognition", "Toutes (chaque réponse IA est ancrée)"],
    },
    en: { title: "Doctrine", purpose: "Versioned epistemological engine.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Doktrin", purpose: "Versionierte epistemologische Maschine.", tabs: [], key_actions: [], related_modules: [] },
  },

  negociation: {
    fr: {
      title: "Négociation",
      purpose:
        "Pilote vos négociations actives. Chaque négo est cadrée par sa structure SCAL (Sujet · Contexte · AMC · Limites) et son score de tension (0-100). Permet de simuler avant de négocier et de remonter les leviers exploitables.",
      how_it_works:
        "Pour chaque négo : sujet, AMC (alternative meilleure que conclure), composants à négocier, tension, flex ratio, liens vers Annuaire et deals. L'IA peut proposer une lecture stratégique avant un round.",
      tabs: [],
      key_actions: [
        "Créer une négociation",
        "Documenter SCAL + composants",
        "Lier à des entités Annuaire et à un deal",
        "Demander une lecture IA pré-round",
      ],
      related_modules: ["Annuaire", "Origination", "Courtage", "Doctrine"],
    },
    en: { title: "Negotiation", purpose: "Active negotiation management with SCAL framing.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Verhandlung", purpose: "Aktive Verhandlungen mit SCAL-Rahmen.", tabs: [], key_actions: [], related_modules: [] },
  },

  chainage: {
    fr: {
      title: "Chaînage causal",
      purpose:
        "Cartographie les chaînes causales de votre organisation : Dans-quel-but ↑ (téléologie) · Comment ↓ (étiologie). Permet de relier opérationnel et stratégique, de détecter les chaînes rompues, et d'attacher chaque deal/risque/négo à sa position dans la chaîne.",
      how_it_works:
        "Chaque chaîne = un graphe orienté (React Flow). Nodes typés (cause, effet, levier, finalité). Les modules (Origination, Risques, Négociation) peuvent référencer un node précis pour montrer leur ancrage.",
      tabs: [],
      key_actions: [
        "Créer une nouvelle chaîne",
        "Ajouter / supprimer / chaîner des nodes",
        "Sélectionner + supprimer en lot, vider une chaîne",
        "Référencer un node depuis un autre module",
      ],
      related_modules: ["Origination", "Risques", "Doctrine", "Cartographie 360"],
    },
    en: { title: "Causal chain", purpose: "What-for ↑ / How ↓ causal mapping.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Kausalkette", purpose: "Wozu ↑ / Wie ↓ Kartierung.", tabs: [], key_actions: [], related_modules: [] },
  },

  cartographie360: {
    fr: {
      title: "Cartographie 360°",
      purpose:
        "Vue 360° d'une entité (personne, société, deal) : tous ses liens cross-modules dans un seul graphe interactif. Annuaire × Pipeline × Négociation × Courtage × Risques × Veille — toutes les connexions remontent ici.",
      how_it_works:
        "Vue graphe interactive. Cliquer un node ouvre sa fiche dans le module d'origine. Filtres par type de lien, profondeur de relation, intensité d'engagement.",
      tabs: [],
      key_actions: [
        "Ouvrir la cartographie depuis n'importe quelle entité",
        "Filtrer par profondeur / type de lien",
        "Exporter en PNG ou en JSON pour archive",
      ],
      related_modules: ["Annuaire", "Origination", "Négociation", "Courtage", "Radar"],
    },
    en: { title: "360 Cartography", purpose: "Cross-module entity 360 view.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "360°-Kartografie", purpose: "Modulübergreifende 360-Ansicht.", tabs: [], key_actions: [], related_modules: [] },
  },

  brvm: {
    fr: {
      title: "Marchés financiers — Market Screener",
      purpose:
        "Module marchés financiers : positions suivies, alertes, screener Value multi-marchés (BRVM, NYSE, NASDAQ, Euronext, LSE, JSE, SIX…). La BRVM (UEMOA) reste un marché supporté parmi les autres.",
      how_it_works:
        "Screener filtre les actions selon des critères Value (PE, PB, dividende, croissance) sur tous les marchés supportés. Les positions suivies génèrent des signaux dans le Radar quand des mouvements critiques surviennent.",
      tabs: [
        { name: "Positions", description: "Vos positions actuelles avec valorisation temps réel approchée." },
        { name: "Value Screener", description: "Screener Benjamin-Graham appliqué aux titres BRVM." },
        { name: "Alertes", description: "Alertes générées sur les actions screened ou suivies." },
      ],
      key_actions: ["Ajouter une position", "Lancer un screening Value", "Configurer une alerte"],
      related_modules: ["Radar", "Annuaire", "Risques"],
    },
    en: { title: "Financial markets", purpose: "BRVM Value Screener module.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Finanzmärkte", purpose: "BRVM-Modul.", tabs: [], key_actions: [], related_modules: [] },
  },

  ontologie: {
    fr: {
      title: "Ontologie",
      purpose:
        "Graphe global de votre organisation. Visualise les types d'entités présentes dans Noam (Annuaire, Pipeline, Risques, Asymétries, Phantoms, Frictions, Doctrine, Chaînages) et leurs interconnexions. C'est la carte mentale de votre environnement.",
      how_it_works:
        "Graphe React Flow regroupant tous les nodes de l'environnement par catégorie. Toggles pour afficher/masquer chaque groupe. Permet de détecter les zones isolées (entités sans liens) et les hubs (entités hyper-connectées).",
      tabs: [],
      key_actions: [
        "Activer/désactiver des groupes de nodes",
        "Identifier les hubs et les zones isolées",
        "Cliquer un node pour ouvrir sa fiche source",
      ],
      related_modules: ["Annuaire", "Risques", "Chaînage", "Origination", "Cartographie 360"],
    },
    en: { title: "Ontology", purpose: "Global graph of your environment entities.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Ontologie", purpose: "Globaler Graph der Umgebungs-Entitäten.", tabs: [], key_actions: [], related_modules: [] },
  },

  trend: {
    fr: {
      title: "Veille stratégique",
      purpose:
        "Agrège les signaux faibles détectés par le Radar et les sources externes. Vue chronologique avec catégorisation, scoring de criticité, et possibilité d'archiver chaque signal dans la Bibliothèque ou de demander un impact évalué.",
      how_it_works:
        "Les signaux remontent du Radar (Tavily + NOAM) avec leur catégorie (macro, géopo, réglementaire, marchés…), région, criticité (faible/moyen/élevé/critique). Vous pouvez filtrer, marquer comme lu, ou demander à NOAM une lecture impact spécifique à votre organisation.",
      tabs: [],
      key_actions: [
        "Filtrer par catégorie / région / criticité",
        "Marquer un signal comme lu",
        "Demander une analyse d'impact NOAM",
        "Archiver dans la Bibliothèque de Briefs",
      ],
      related_modules: ["Radar", "Annuaire", "Bibliothèque de briefs", "Intelligence IA"],
    },
    en: { title: "Strategic watch", purpose: "Aggregated weak signals timeline.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Strategische Beobachtung", purpose: "Chronologie der schwachen Signale.", tabs: [], key_actions: [], related_modules: [] },
  },

  risques: {
    fr: {
      title: "Gestion des risques",
      purpose:
        "Cartographie des risques de votre organisation. Pour chaque risque : catégorie, impact net (1-5), probabilité nette (1-5), tendance (↑↓→), liens vers chaînages causaux et entités Annuaire. Le Top-5 critique remonte dans l'Intelligence IA et l'Executive 360°.",
      how_it_works:
        "Score de sévérité = impact × probabilité (max 25). Les risques peuvent être reliés à des nodes de chaînage causal pour visualiser leur position dans la chaîne. Tendance manuelle ou inférée par NOAM.",
      tabs: [],
      key_actions: [
        "Ajouter / éditer un risque",
        "Lier à un node de chaînage causal",
        "Demander une lecture IA des risques par catégorie",
        "Exporter une matrice risques en PDF",
      ],
      related_modules: ["Chaînage", "Doctrine", "Intelligence IA", "Annuaire"],
    },
    en: { title: "Risk management", purpose: "Organisation risk mapping.", tabs: [], key_actions: [], related_modules: [] },
    de: { title: "Risikomanagement", purpose: "Risikoabbildung der Organisation.", tabs: [], key_actions: [], related_modules: [] },
  },
};
